/**
 * Parser for the agent→client `[RESOLVER_OPTIONS]` render tag
 * (claw-doctrine §5.6.2). Split from MessageBubble.tsx so the parser
 * is unit-testable without the component file having to export
 * non-component values (react-refresh enforcement).
 *
 * The tag carries the JSON payload that drives the in-bubble
 * ResolverChoice widget (A/B/C buttons + free-text override). The
 * shape guard `isResolverOptionsPayload` validates the wire shape so
 * malformed agent output never reaches the renderer.
 *
 * Behavior contract (pinned by
 * `test/unit/components/chat/MessageBubble.resolverOptions.test.tsx`):
 *
 *   - well-formed tag → parsed payload + tag stripped
 *   - malformed JSON  → null payload, tag still stripped
 *   - shape mismatch  → null payload, tag still stripped
 *   - multiple tags   → first only; all tags stripped
 *   - no tag          → original content unchanged, null payload
 */

import type { ResolverOptionsPayload } from "@/components/chat/ResolverChoice";

const RESOLVER_OPTIONS_RE = /\[RESOLVER_OPTIONS\]\s*(\{[\s\S]*?\})\s*\[\/RESOLVER_OPTIONS\]/g;

function isResolverOptionsPayload(value: unknown): value is ResolverOptionsPayload {
  if (typeof value !== "object" || value === null) return false;
  const v = value as Record<string, unknown>;
  const isLabel = (x: unknown): x is { label: string } =>
    typeof x === "object" && x !== null && typeof (x as { label?: unknown }).label === "string";
  const isPlaceholder = (x: unknown): x is { placeholder: string } =>
    typeof x === "object" &&
    x !== null &&
    typeof (x as { placeholder?: unknown }).placeholder === "string";
  return (
    typeof v.context === "string" &&
    isLabel(v.optionA) &&
    isLabel(v.optionB) &&
    isPlaceholder(v.optionC)
  );
}

export function extractResolverOptions(content: string): {
  cleanContent: string;
  options: ResolverOptionsPayload | null;
} {
  let options: ResolverOptionsPayload | null = null;
  const cleanContent = content
    .replace(RESOLVER_OPTIONS_RE, (_match, json) => {
      if (options === null) {
        try {
          const parsed = JSON.parse(json);
          if (isResolverOptionsPayload(parsed)) options = parsed;
        } catch {
          /* shape failure — strip the tag, no widget rendered */
        }
      }
      return "";
    })
    .trim();
  return { cleanContent, options };
}
